import React, { useRef } from 'react'
import { Spin } from 'antd'
import { useElementSize } from '@pushly/aqe/lib/hooks'
import { getClassNames } from '../../_utils/classnames'
import {
    getVisiblePages,
    IResolvedPage,
    ISettingsContext,
    ISettingsGroup,
    ISettingsNavGroup,
    ISettingsPage,
    resolveSettingsPage,
    SETTINGS_GROUPS,
    SETTINGS_PAGES,
} from './registry'
import { SettingsNarrowNav } from './settings-narrow-nav'
import { SettingsRail } from './settings-rail'

import './settings-layout.scss'

/** Below this content-area width the rail gives way to two-tier tabs. */
export const RAIL_MIN_WIDTH = 720

interface ISettingsLayout<C> {
    hash: string
    context: C
    onNavigate: (key: string) => void
    /** The domain's pages and groups unless given, so the organization can lay out its own the same way. */
    pages?: ISettingsPage<C>[]
    groups?: ISettingsGroup[]
    /** Which settings rail this is, for its Pushly icons: the domain's unless given. */
    iconContext?: 'domain' | 'org'
}

export const SettingsLayout = <C = ISettingsContext,>({
    hash,
    context,
    onNavigate,
    pages = SETTINGS_PAGES as unknown as ISettingsPage<C>[],
    groups: groupDefs = SETTINGS_GROUPS,
    iconContext = 'domain',
}: ISettingsLayout<C>) => {
    const ref = useRef<HTMLDivElement>(null)
    const { width } = useElementSize(ref)

    const visible = getVisiblePages(context, pages)
    const { page, loading }: IResolvedPage<C> = resolveSettingsPage(hash, context, pages)
    const groups: ISettingsNavGroup<C>[] = groupDefs
        .map((group) => ({
            ...group,
            pages: visible.filter((p) => p.group === group.key),
        }))
        .filter((group) => group.pages.length > 0)

    // Pages whose flags have not resolved yet are not listed; their rows are held so the rail does not jump.
    const pendingCount = pages.filter((p) => p.visible(context) === 'loading').length

    // Width is 0 until the first measurement; render the rail then so there is no flash of tabs.
    const showRail = width === 0 || width >= RAIL_MIN_WIDTH

    const content =
        loading && page.group !== 'ai' ? (
            <Spin className="settings-loading" />
        ) : (
            <div key={page.mountKey ?? page.key} className="settings-content">
                {page.render(context)}
            </div>
        )

    return (
        <div ref={ref} className={getClassNames('settings-layout', showRail ? 'with-rail' : 'with-tabs')}>
            {showRail ? (
                <SettingsRail
                    groups={groups}
                    currentKey={page.key}
                    onNavigate={onNavigate}
                    pendingCount={pendingCount}
                    iconContext={iconContext}
                />
            ) : (
                <SettingsNarrowNav groups={groups} page={page} onNavigate={onNavigate} />
            )}
            <div className="settings-main">{content}</div>
        </div>
    )
}
